/**
 * Application router.
 *
 *   /login, /support, /privacy, *  → public auth surfaces
 *   /super_admin/*                  → super_admin role tree
 *   /admin/*                        → company-admin role tree
 *   /shore_staff/*                  → shore_staff role tree
 *   /<everything else>              → vessel role tree (its base is empty)
 *
 * Pages are lazy-loaded for chunking; layouts wrap each role tree.
 */
import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate, RouterProvider, useLocation } from "react-router-dom";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import RequireFeature from "@/components/auth/RequireFeature";
import { Layout } from "@/components/layouts/Layout";
import { RouteErrorBoundary } from "@/components/ErrorBoundary";
import { OnlineOnly } from "@/components/common/offline-unavailable";
import { useAuth } from "@/contexts/AuthContext";
import { roleHomePath } from "./role-home";
// Login is EAGERLY imported (part of the main bundle), not lazy: it's the
// bootstrap destination and, crucially, where logout lands. A lazy chunk can
// fail to fetch when you log out OFFLINE (no network / SW miss), which would
// bubble past this route (it has no errorElement) to the global error screen
// instead of showing the login page. Bundling it guarantees it always renders.
import Login from "@/pages/auth/Login";

/* ─────────── Lazy pages ─────────── */
const ActivateAdmin  = lazy(() => import("@/pages/auth/ActivateAdmin"));
const ForgotPassword = lazy(() => import("@/pages/auth/ForgotPassword"));
const ResetPassword  = lazy(() => import("@/pages/auth/ResetPassword"));
const Support        = lazy(() => import("@/pages/common/Support"));
// Privacy / EULA / Terms are one bilingual tabbed page (replicated from
// icheck-chinese); each legal route just opens its corresponding tab.
const PrivacyPolicy  = lazy(() => import("@/pages/common/PrivacyPolicy"));
const NotFound       = lazy(() => import("@/pages/common/NotFound"));

// Help Center — public, standalone (outside the authenticated Layout).
const HelpCenter     = lazy(() => import("@/pages/help/HelpCenter"));
const HelpArticle    = lazy(() => import("@/pages/help/HelpArticle"));

// Profile was merged into CompanySettings. The /profile route now redirects
// to /settings (see role sections below); keep the page file on disk for one
// more cycle in case anyone still has a hand-saved deep-link.
const Notifications = lazy(() => import("@/pages/common/Notifications"));
const AI            = lazy(() => import("@/pages/common/AI"));

const VesselDashboard      = lazy(() => import("@/pages/vessel/Dashboard"));
const CrewManagement       = lazy(() => import("@/pages/vessel/CrewManagement"));
const InspectorList        = lazy(() => import("@/pages/admin/InspectorList"));
const InspectorProfile     = lazy(() => import("@/pages/admin/InspectorProfile"));
const InspectionTypes      = lazy(() => import("@/pages/admin/InspectionTypes"));
const CompanyDashboard     = lazy(() => import("@/pages/company-admin/Dashboard"));
const SuperAdminDashboard  = lazy(() => import("@/pages/super_admin/Dashboard"));
const PscCodeManagement    = lazy(() => import("@/pages/super_admin/PscCodeManagement"));
const RankManagement       = lazy(() => import("@/pages/super_admin/RankManagement"));
const BackgroundJobs       = lazy(() => import("@/pages/super_admin/BackgroundJobs"));
const TmsaQuestionBank     = lazy(() => import("@/pages/super_admin/TmsaQuestionBank"));
const ShoreStaffDashboard  = lazy(() => import("@/pages/shore_staff/Dashboard"));

const InspectionList         = lazy(() => import("@/pages/inspections/InspectionList"));
const UpsertInspection       = lazy(() => import("@/pages/inspections/UpsertInspection"));
const NewInspectionWizard    = lazy(() => import("@/pages/inspections/NewInspectionWizard"));
const InspectionDetail       = lazy(() => import("@/pages/inspections/InspectionDetail"));
const InspectionAttachments  = lazy(() => import("@/pages/inspections/InspectionAttachments"));
const InspectionQuestions    = lazy(() => import("@/pages/inspections/InspectionWorkflow"));
const InspectionPrep         = lazy(() => import("@/pages/inspections/InspectionPrep"));
const SireImport             = lazy(() => import("@/pages/inspections/SireImport"));
const ReportImport           = lazy(() => import("@/pages/inspections/report-import/ReportImport"));
const PreInspectionList      = lazy(() => import("@/pages/inspections/PreInspectionList"));
const UpsertPreInspection    = lazy(() => import("@/pages/inspections/UpsertPreInspection"));

const ObservationList    = lazy(() => import("@/pages/observations/ObservationList"));
// One route component for both issue kinds (observation + deficiency finding).
const IssueDetail        = lazy(() => import("@/pages/IssueDetail"));
const RectificationsPage = lazy(() => import("@/pages/rectifications/RectificationsPage"));
const ChecklistList      = lazy(() => import("@/pages/checklists/ChecklistList"));
const ChecklistEditor    = lazy(() => import("@/pages/checklists/ChecklistEditor"));
const CompanyList        = lazy(() => import("@/pages/admin/CompanyList"));
const UpsertCompany      = lazy(() => import("@/pages/admin/UpsertCompany"));
const VesselList         = lazy(() => import("@/pages/admin/VesselList"));
const UpsertVessel       = lazy(() => import("@/pages/admin/UpsertVessel"));
const UserList           = lazy(() => import("@/pages/admin/UserList"));
const CrewList           = lazy(() => import("@/pages/admin/CrewList"));
const RoleManagement     = lazy(() => import("@/pages/admin/RoleManagement"));
const UpsertUser         = lazy(() => import("@/pages/admin/UpsertUser"));
const CompanySettings    = lazy(() => import("@/pages/admin/CompanySettings"));
const TmsaDashboard      = lazy(() => import("@/pages/tmsa/TmsaDashboard"));
const TmsaEditor         = lazy(() => import("@/pages/tmsa/TmsaEditor"));
const PlatformAiFeatures = lazy(() => import("@/pages/admin/PlatformAiFeatures"));
const DrybmsDashboard    = lazy(() => import("@/pages/drybms/DrybmsDashboard"));
const DrybmsEditor       = lazy(() => import("@/pages/drybms/DrybmsEditor"));
const DrybmsFramework    = lazy(() => import("@/pages/super_admin/DrybmsFramework"));
const SmsLibrary         = lazy(() => import("@/pages/sms/SmsLibrary"));
const SmsDocument        = lazy(() => import("@/pages/sms/SmsDocument"));
const SmsBuilder         = lazy(() => import("@/pages/sms/SmsBuilder"));
const SmsInsights        = lazy(() => import("@/pages/sms/SmsInsights"));
const RootCauseCategoriesPage = lazy(() => import("@/pages/settings/RootCauseCategories"));

const FleetAnalytics     = lazy(() => import("@/pages/analytics/FleetAnalytics"));
const VettingPriorityQueue = lazy(() => import("@/pages/vetting/VettingPriorityQueue"));
const RiskHeatMap        = lazy(() => import("@/pages/analytics/RiskHeatMap"));
const CycleComparison    = lazy(() => import("@/pages/analytics/CycleComparison"));
const PeriodicReports    = lazy(() => import("@/pages/analytics/PeriodicReports"));
const HistoricalImport   = lazy(() => import("@/pages/analytics/HistoricalImport"));

const PageFallback = () => (
  <div className="grid place-items-center p-12">
    <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
  </div>
);

const wrap = (node: React.ReactNode) => <Suspense fallback={<PageFallback />}>{node}</Suspense>;

/** The report import's old address, from when it only read SIRE reports
 *  (…/manage-inspections/sire-import). Kept so bookmarks still land on the
 *  screen. Rewrites the URL itself rather than a relative path, so it works
 *  under every role's base and however the route is nested. */
function OldReportImportRedirect() {
  const { pathname, search } = useLocation();
  return <Navigate to={pathname.replace(/\/sire-import\/?$/, "/import-report") + search} replace />;
}

/* ─────────── Root index — sends user to their role home ─────────── */
function RootIndex() {
  const { user, loading } = useAuth();
  if (loading) return <PageFallback />;
  return <Navigate to={user ? roleHomePath(user.role) : "/login"} replace />;
}

/* ─────────── Router definition ─────────── */
const router = createBrowserRouter([
  /* Public auth surfaces */
  { path: "/login",           element: wrap(<Login />) },
  { path: "/activate-admin",  element: wrap(<ActivateAdmin />) },
  { path: "/forgot-password", element: wrap(<ForgotPassword />) },
  { path: "/reset-password",  element: wrap(<ResetPassword />) },
  { path: "/support",         element: wrap(<Support />) },
  { path: "/privacy",         element: wrap(<PrivacyPolicy initialTab="privacyPolicy" />) },
  { path: "/eula",            element: wrap(<PrivacyPolicy initialTab="eula" />) },
  { path: "/terms",           element: wrap(<PrivacyPolicy initialTab="tc" />) },

  /* Help Center (public, standalone) */
  { path: "/help",            element: wrap(<HelpCenter />),    errorElement: <RouteErrorBoundary /> },
  { path: "/help/:slug",      element: wrap(<HelpArticle />),   errorElement: <RouteErrorBoundary /> },

  /* Root → role home */
  { path: "/", element: <RootIndex /> },

  /* ── Super-admin ── */
  {
    path: "/super_admin",
    element: <ProtectedRoute roles={["super_admin"]} />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <Layout />,
        errorElement: <RouteErrorBoundary />,
        children: [
          { index: true,                element: <Navigate to="dashboard" replace /> },
          { path: "dashboard",          element: wrap(<SuperAdminDashboard />) },
          { path: "ai",                 element: <Navigate to="../dashboard" replace /> },
          { path: "profile",            element: <Navigate to="../settings" replace /> },
          { path: "settings",           element: wrap(<CompanySettings />) },
          // Root cause list is Company Admin only — redirect super_admin away.
          { path: "root-cause-categories",            element: <Navigate to="../dashboard" replace /> },
          { path: "notifications",      element: wrap(<Notifications />) },

          { path: "manage-inspections",                   element: wrap(<InspectionList />) },
          // /import is Company Admin only — redirect anyone else who tries.
          { path: "manage-inspections/import",            element: <Navigate to="../manage-inspections" replace /> },
          { path: "manage-inspections/new",               element: wrap(<NewInspectionWizard />) },
          { path: "manage-inspections/:id",               element: wrap(<InspectionDetail />) },
          { path: "manage-inspections/:id/edit",          element: wrap(<UpsertInspection />) },
          { path: "manage-inspections/:id/attachments",   element: wrap(<InspectionAttachments />) },
          { path: "manage-inspections/:id/questions",     element: wrap(<InspectionQuestions />) },
          { path: "manage-inspections/:id/prepare",       element: wrap(<InspectionPrep />) },

          { path: "pre-inspections",                      element: wrap(<InspectionList fixedType="piq" title="Pre-inspections (PIQ)" description="Pre-Inspection Questionnaires for your fleet." />) },
          { path: "pre-inspections/new",                  element: wrap(<UpsertPreInspection />) },
          { path: "pre-inspections/:id",                  element: wrap(<InspectionDetail />) },
          { path: "pre-inspections/:id/edit",             element: wrap(<UpsertPreInspection />) },
          { path: "pre-inspections/:id/attachments",      element: wrap(<InspectionAttachments />) },
          { path: "pre-inspections/:id/questions",        element: wrap(<InspectionQuestions />) },

          { path: "company-management",                   element: wrap(<CompanyList />) },
          { path: "company-management/new",               element: wrap(<UpsertCompany />) },
          { path: "company-management/:id/edit",          element: wrap(<UpsertCompany />) },
          { path: "company-management/:id/settings",      element: wrap(<CompanySettings />) },

          { path: "vessel-management",                    element: <Navigate to="../company-management" replace /> },
          { path: "vessel-management/*",                  element: <Navigate to="../company-management" replace /> },
          { path: "user-management",                      element: <Navigate to="../company-management" replace /> },
          { path: "user-management/*",                    element: <Navigate to="../company-management" replace /> },

          { path: "checklist-management",                 element: wrap(<ChecklistList />) },
          { path: "checklist-management/:id",             element: wrap(<ChecklistEditor />) },

          { path: "psc-codes",                            element: wrap(<PscCodeManagement />) },
          { path: "ranks",                                element: wrap(<RankManagement />) },
          { path: "background-jobs",                      element: wrap(<BackgroundJobs />) },
          { path: "tmsa-question-bank",                   element: wrap(<TmsaQuestionBank />) },
          { path: "ai-features",                          element: wrap(<PlatformAiFeatures />) },
          { path: "drybms-framework",                     element: wrap(<DrybmsFramework />) },

          { path: "observations",                         element: wrap(<ObservationList />) },
          { path: "observations/:id",                     element: wrap(<IssueDetail kind="observation" />) },
          { path: "findings/:id",                         element: wrap(<IssueDetail kind="finding" />) },

          { path: "risk-heat-map",                        element: <Navigate to="../dashboard" replace /> },
          { path: "cycle-comparison",                     element: <Navigate to="../dashboard" replace /> },
          { path: "reports",                              element: <Navigate to="../dashboard" replace /> },
          { path: "import-data",                          element: <Navigate to="../dashboard" replace /> },
        ],
      },
    ],
  },

  /* ── Company admin ── */
  {
    path: "/admin",
    element: <ProtectedRoute roles={["admin"]} />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <Layout />,
        errorElement: <RouteErrorBoundary />,
        children: [
          { index: true,                element: <Navigate to="dashboard" replace /> },
          { path: "dashboard",          element: wrap(<CompanyDashboard />) },
          { path: "ai",                 element: wrap(<AI />) },
          { path: "profile",            element: <Navigate to="../settings" replace /> },
          { path: "notifications",      element: wrap(<Notifications />) },

          { path: "manage-inspections",                   element: wrap(<InspectionList />) },
          { path: "manage-inspections/import",            element: wrap(<HistoricalImport />) },
          { path: "manage-inspections/import-report",     element: wrap(<SireImport />) },
          { path: "manage-inspections/import-deficiency-report", element: wrap(<ReportImport />) },
          { path: "manage-inspections/sire-import",       element: <OldReportImportRedirect /> },
          { path: "manage-inspections/new",               element: wrap(<NewInspectionWizard />) },
          { path: "manage-inspections/:id",               element: wrap(<InspectionDetail />) },
          { path: "manage-inspections/:id/edit",          element: wrap(<UpsertInspection />) },
          { path: "manage-inspections/:id/attachments",   element: wrap(<InspectionAttachments />) },
          { path: "manage-inspections/:id/questions",     element: wrap(<InspectionQuestions />) },
          { path: "manage-inspections/:id/prepare",       element: wrap(<InspectionPrep />) },

          { path: "pre-inspections",                      element: wrap(<InspectionList fixedType="piq" title="Pre-inspections (PIQ)" description="Pre-Inspection Questionnaires for your fleet." />) },
          { path: "pre-inspections/new",                  element: wrap(<UpsertPreInspection />) },
          { path: "pre-inspections/:id",                  element: wrap(<InspectionDetail />) },
          { path: "pre-inspections/:id/edit",             element: wrap(<UpsertPreInspection />) },
          { path: "pre-inspections/:id/attachments",      element: wrap(<InspectionAttachments />) },
          { path: "pre-inspections/:id/questions",        element: wrap(<InspectionQuestions />) },

          { path: "company-management",                   element: wrap(<CompanyList />) },
          { path: "company-management/new",               element: wrap(<UpsertCompany />) },
          { path: "company-management/:id/edit",          element: wrap(<UpsertCompany />) },

          { path: "vessel-management",                    element: wrap(<VesselList />) },
          { path: "vessel-management/new",                element: wrap(<UpsertVessel />) },
          { path: "vessel-management/:id/edit",           element: wrap(<UpsertVessel />) },

          { path: "user-management",                      element: wrap(<UserList />) },
          { path: "user-management/roles",                element: wrap(<RoleManagement />) },
          { path: "crew",                                 element: wrap(<CrewList />) },
          { path: "inspector-management",                 element: wrap(<InspectorList />) },
          { path: "inspector-management/:id",             element: wrap(<InspectorProfile />) },
          { path: "user-management/new",                  element: wrap(<UpsertUser />) },
          { path: "user-management/:id/edit",             element: wrap(<UpsertUser />) },

          { path: "checklist-management",                 element: wrap(<ChecklistList />) },
          { path: "checklist-management/:id",             element: wrap(<ChecklistEditor />) },
          { path: "finding-types",                        element: wrap(<InspectionTypes />) },

          { path: "observations",                         element: wrap(<ObservationList />) },

          // TMSA lives in the ADMIN shell only. It is gated on tmsa:* which
          // shore_staff and vessel do not get, and a super_admin has no
          // company context — the API would answer 400 for them.
          { path: "tmsa",                                 element: wrap(<TmsaDashboard />) },
          { path: "tmsa/:id",                             element: wrap(<TmsaEditor />) },
          // DryBMS — same admin-shell gating as TMSA (drybms:* permission).
          { path: "drybms",                               element: wrap(<DrybmsDashboard />) },
          { path: "drybms/:id",                           element: wrap(<DrybmsEditor />) },
          // SMS library — every role reads it (sms_documents:view).
          { element: <RequireFeature feature="sms_documents" />, children: [
            { path: "sms",                                element: wrap(<SmsLibrary />) },
            { path: "sms/:id",                            element: wrap(<SmsDocument />) },
            { path: "sms/new",                            element: wrap(<SmsBuilder />) },
            { path: "sms/insights",                       element: wrap(<SmsInsights />) },
            { path: "sms/:id/edit",                       element: wrap(<SmsBuilder />) },
          ] },
          { path: "observations/:id",                     element: wrap(<IssueDetail kind="observation" />) },
          { path: "findings/:id",                         element: wrap(<IssueDetail kind="finding" />) },

          // Analytics routes are RBAC-gated so a deep-link can't bypass a
          // disabled feature (the sidebar hides them, RequireFeature blocks
          // direct navigation, the backend re-checks the permission).
          { element: <RequireFeature feature="fleet_analytics" />, children: [
            { path: "analytics",                          element: wrap(<FleetAnalytics />) },
            { path: "vetting-queue",                      element: wrap(<VettingPriorityQueue />) },
          ] },
          { element: <RequireFeature feature="risk_heat_map" />, children: [
            { path: "risk-heat-map",                      element: wrap(<RiskHeatMap />) },
          ] },
          { element: <RequireFeature feature="cycle_comparison" />, children: [
            { path: "cycle-comparison",                   element: wrap(<CycleComparison />) },
          ] },
          { element: <RequireFeature feature="reports" />, children: [
            { path: "reports",                            element: wrap(<PeriodicReports />) },
          ] },
          // Relocated: the PDF report import (SIRE 2.0 and RISQ 3.2) now lives
          // as a button on the Inspections page
          // (manage-inspections/import-report). Old bookmarks to
          // /admin/import-data redirect there.
          { path: "import-data",                          element: <Navigate to="../manage-inspections/import-report" replace /> },

          { path: "settings",                             element: wrap(<CompanySettings />) },
          { path: "root-cause-categories",            element: wrap(<RootCauseCategoriesPage />) },
        ],
      },
    ],
  },

  /* ── Shore-staff ── */
  {
    path: "/shore_staff",
    element: <ProtectedRoute roles={["shore_staff"]} />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <Layout />,
        errorElement: <RouteErrorBoundary />,
        children: [
          { index: true,                element: <Navigate to="dashboard" replace /> },
          { path: "dashboard",          element: wrap(<ShoreStaffDashboard />) },
          { path: "profile",            element: <Navigate to="../settings" replace /> },
          { path: "settings",           element: wrap(<CompanySettings />) },
          { path: "notifications",      element: wrap(<Notifications />) },
          // Single-shell: shore_staff uses the admin shell; each Manage/Analytics
          // module is RequireFeature-gated so a plain shore_staff can't reach it,
          // but a shore-workspace role granted the feature can.
          { element: <RequireFeature feature="ai_insights" />, children: [
            { path: "ai",                                 element: wrap(<AI />) },
          ] },
          { element: <RequireFeature feature="root_causes" />, children: [
            { path: "root-cause-categories",              element: wrap(<RootCauseCategoriesPage />) },
          ] },

          { path: "manage-inspections",                   element: wrap(<InspectionList />) },
          { element: <RequireFeature feature="import_data" />, children: [
            { path: "manage-inspections/import",          element: wrap(<HistoricalImport />) },
            { path: "manage-inspections/import-report",   element: wrap(<SireImport />) },
            { path: "manage-inspections/import-deficiency-report", element: wrap(<ReportImport />) },
            { path: "manage-inspections/sire-import",     element: <OldReportImportRedirect /> },
          ] },
          { path: "manage-inspections/new",               element: wrap(<NewInspectionWizard />) },
          { path: "manage-inspections/:id",               element: wrap(<InspectionDetail />) },
          { path: "manage-inspections/:id/edit",          element: wrap(<UpsertInspection />) },
          { path: "manage-inspections/:id/attachments",   element: wrap(<InspectionAttachments />) },
          { path: "manage-inspections/:id/questions",     element: wrap(<InspectionQuestions />) },
          { path: "manage-inspections/:id/prepare",       element: wrap(<InspectionPrep />) },

          { path: "pre-inspections",                      element: wrap(<InspectionList fixedType="piq" title="Pre-inspections (PIQ)" description="Pre-Inspection Questionnaires for your fleet." />) },
          { path: "pre-inspections/new",                  element: wrap(<UpsertPreInspection />) },
          { path: "pre-inspections/:id",                  element: wrap(<InspectionDetail />) },
          { path: "pre-inspections/:id/edit",             element: wrap(<UpsertPreInspection />) },
          { path: "pre-inspections/:id/attachments",      element: wrap(<InspectionAttachments />) },
          { path: "pre-inspections/:id/questions",        element: wrap(<InspectionQuestions />) },

          { path: "observations",                         element: wrap(<ObservationList />) },
          { path: "observations/:id",                     element: wrap(<IssueDetail kind="observation" />) },
          { path: "findings/:id",                         element: wrap(<IssueDetail kind="finding" />) },

          // SMS library — read and searched offline from the warmed copy.
          { element: <RequireFeature feature="sms_documents" />, children: [
            { path: "sms",                                element: wrap(<SmsLibrary />) },
            { path: "sms/:id",                            element: wrap(<SmsDocument />) },
            { path: "sms/new",                            element: wrap(<SmsBuilder />) },
            { path: "sms/insights",                       element: wrap(<SmsInsights />) },
            { path: "sms/:id/edit",                       element: wrap(<SmsBuilder />) },
          ] },

          // Checklists are a Manage feature, but a shore-workspace role can be
          // granted "checklists" — the guard blocks it otherwise (and the nav
          // item stays hidden), so a plain shore_staff can't deep-link in.
          { element: <RequireFeature feature="checklists" />, children: [
            { path: "checklist-management",               element: wrap(<ChecklistList />) },
            { path: "checklist-management/:id",           element: wrap(<ChecklistEditor />) },
          ] },
          { element: <RequireFeature feature="finding_types" />, children: [
            { path: "finding-types",                      element: wrap(<InspectionTypes />) },
          ] },
          { element: <RequireFeature feature="vessels" />, children: [
            { path: "vessel-management",                  element: wrap(<VesselList />) },
            { path: "vessel-management/new",              element: wrap(<UpsertVessel />) },
            { path: "vessel-management/:id/edit",         element: wrap(<UpsertVessel />) },
          ] },
          { element: <RequireFeature feature="users" />, children: [
            { path: "user-management",                    element: wrap(<UserList />) },
            { path: "user-management/roles",              element: wrap(<RoleManagement />) },
            { path: "crew",                               element: wrap(<CrewList />) },
            { path: "user-management/new",                element: wrap(<UpsertUser />) },
            { path: "user-management/:id/edit",           element: wrap(<UpsertUser />) },
            { path: "inspector-management",               element: wrap(<InspectorList />) },
            { path: "inspector-management/:id",           element: wrap(<InspectorProfile />) },
          ] },

          // RBAC-gated (see admin group above) so shore_staff can't deep-link
          // to an Analytics feature the company admin disabled for the role.
          { element: <RequireFeature feature="fleet_analytics" />, children: [
            { path: "analytics",                          element: wrap(<FleetAnalytics />) },
            { path: "vetting-queue",                      element: wrap(<VettingPriorityQueue />) },
          ] },
          { element: <RequireFeature feature="risk_heat_map" />, children: [
            { path: "risk-heat-map",                      element: wrap(<RiskHeatMap />) },
          ] },
          { element: <RequireFeature feature="cycle_comparison" />, children: [
            { path: "cycle-comparison",                   element: wrap(<CycleComparison />) },
          ] },
          { element: <RequireFeature feature="reports" />, children: [
            { path: "reports",                            element: wrap(<PeriodicReports />) },
          ] },
        ],
      },
    ],
  },

  /* ── Vessel (root-anchored) ── */
  {
    element: <ProtectedRoute roles={["vessel"]} />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <Layout />,
        errorElement: <RouteErrorBoundary />,
        children: [
          { path: "/dashboard",         element: wrap(<VesselDashboard />) },
          // AI tooling is Company Admin only — redirect vessel back.
          { path: "/ai",                element: <Navigate to="/dashboard" replace /> },
          { path: "/profile",           element: <Navigate to="/settings" replace /> },
          { path: "/settings",          element: wrap(<CompanySettings />) },
          { path: "/notifications",     element: wrap(<Notifications />) },
          // Crew logins + inspectors are management screens that need live data
          // and aren't useful offline — show a "needs connection" notice instead.
          { path: "/crew-management",   element: <OnlineOnly feature="Crew">{wrap(<CrewManagement />)}</OnlineOnly> },
          { path: "/inspector-management",        element: <OnlineOnly feature="Inspectors">{wrap(<InspectorList />)}</OnlineOnly> },
          { path: "/inspector-management/:id",    element: <OnlineOnly feature="Inspector details">{wrap(<InspectorProfile />)}</OnlineOnly> },

          { path: "/manage-inspections",                  element: wrap(<InspectionList />) },
          { path: "/manage-inspections/new",              element: wrap(<NewInspectionWizard />) },
          { path: "/manage-inspections/:id",              element: wrap(<InspectionDetail />) },
          { path: "/manage-inspections/:id/edit",         element: wrap(<UpsertInspection />) },
          { path: "/manage-inspections/:id/attachments",  element: wrap(<InspectionAttachments />) },
          { path: "/manage-inspections/:id/questions",    element: wrap(<InspectionQuestions />) },
          { path: "/manage-inspections/:id/prepare",      element: wrap(<InspectionPrep />) },

          { path: "/pre-inspections",                     element: wrap(<InspectionList fixedType="piq" title="Pre-inspections (PIQ)" description="Pre-Inspection Questionnaires for your vessel." />) },
          { path: "/pre-inspections/new",                 element: wrap(<UpsertPreInspection />) },
          { path: "/pre-inspections/:id",                 element: wrap(<InspectionDetail />) },
          { path: "/pre-inspections/:id/edit",            element: wrap(<UpsertPreInspection />) },
          { path: "/pre-inspections/:id/attachments",     element: wrap(<InspectionAttachments />) },
          { path: "/pre-inspections/:id/questions",       element: wrap(<InspectionQuestions />) },

          { path: "/observations",                        element: wrap(<ObservationList />) },
          { path: "/observations/:id",                    element: wrap(<IssueDetail kind="observation" />) },
          { path: "/findings/:id",                        element: wrap(<IssueDetail kind="finding" />) },
          // Rectification queue — crew see their assignments, Master sees the
          // whole vessel. Offline-capable: the page reads the warmed mirror via
          // readThrough (writes still queue through the outbox), so it must NOT
          // be wrapped in OnlineOnly — that guard showed "not available offline"
          // even though the data is cached.
          { path: "/rectifications",                      element: wrap(<RectificationsPage />) },
          // SMS library — offline-capable (warmed copy), so not OnlineOnly.
          { element: <RequireFeature feature="sms_documents" />, children: [
            { path: "/sms",                               element: wrap(<SmsLibrary />) },
            { path: "/sms/:id",                           element: wrap(<SmsDocument />) },
          ] },
        ],
      },
    ],
  },

  /* 404 */
  { path: "*", element: wrap(<NotFound />), errorElement: <RouteErrorBoundary /> },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
